import { useEffect, useState } from 'react';
import { apiService } from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiError';
import { useOptionalMeetSession } from '../video-consult';
import {
  inferVideoProvider,
  openMeetInNewTab,
  videoConsultProviderLabel,
  withPortalAccessToken,
  withStaffRoomRole,
} from '../../utils/videoConsultLabel';

type Props = {
  visitULID: string;
  initialMeetUrl?: string | null;
  disabled?: boolean;
  isCorporate?: boolean;
  onMeetUrlResolved?: (url: string) => void;
};

export function CopyMeetLinkButton({
  visitULID,
  initialMeetUrl,
  disabled,
  isCorporate,
  onMeetUrlResolved,
}: Props) {
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cachedUrl, setCachedUrl] = useState(initialMeetUrl?.trim() || null);
  const [cachedProvider, setCachedProvider] = useState<string | null>(null);
  const providerLabel = videoConsultProviderLabel(isCorporate ? 'corporate' : null, cachedUrl, cachedProvider);
  const meetDock = useOptionalMeetSession();
  const thisVisitInDock = Boolean(meetDock?.enabled && meetDock.isThisVisit(visitULID));

  useEffect(() => {
    const url = initialMeetUrl?.trim();
    if (url) setCachedUrl(url);
  }, [initialMeetUrl]);

  const resolveUrl = async (): Promise<string | null> => {
    const res = await apiService.generateConsultLink(visitULID);
    if (res.googleMeetSkipped && !isCorporate) {
      window.alert(`${providerLabel} is not enabled on the server for this environment.`);
      return null;
    }
    const url = res.googleMeetUrl?.trim() || cachedUrl;
    if (res.videoProvider)
      setCachedProvider(res.videoProvider);
    if (url) {
      setCachedUrl(url);
      onMeetUrlResolved?.(url);
      return url;
    }
    window.alert(`No ${providerLabel} link is available for this visit yet.`);
    return null;
  };

  const handleCopy = async () => {
    setLoading(true);
    try {
      const url = await resolveUrl();
      if (!url) return;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      window.alert(getApiErrorMessage(err, `Could not copy ${providerLabel} link. Please try again.`));
    } finally {
      setLoading(false);
    }
  };

  const handleJoinAsStaff = async () => {
    if (thisVisitInDock) {
      meetDock?.setMode(meetDock.mode === 'full' ? 'mini' : 'full');
      return;
    }
    setLoading(true);
    try {
      const url = await resolveUrl();
      if (!url) return;
      const staffUrl = withStaffRoomRole(url);
      if (inferVideoProvider(url) === 'Jitsi' && meetDock?.enabled) {
        meetDock.startMeet({ visitUlid: visitULID, role: 'staff', joinUrl: staffUrl });
        return;
      }
      openMeetInNewTab(withPortalAccessToken(staffUrl));
    } catch (err) {
      window.alert(getApiErrorMessage(err, `Could not open ${providerLabel}. Please try again.`));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="staff-tele-btn staff-tele-btn--meetlink"
        disabled={disabled || loading}
        onClick={() => void handleJoinAsStaff()}
        title="Join Aspen Meet as yourself (moderator). Video stays on this page so you can go back to the queue."
      >
        {loading
          ? 'Loading…'
          : thisVisitInDock
            ? (meetDock?.mode === 'full' ? 'Minimize video' : 'Show video')
            : 'Join as staff'}
      </button>
      <button
        type="button"
        className="staff-tele-btn staff-tele-btn--meetlink"
        disabled={disabled || loading}
        onClick={() => void handleCopy()}
        title={`Copy ${providerLabel} link to share with the patient`}
      >
        {loading ? 'Loading…' : copied ? 'Copied!' : `Copy ${providerLabel} link`}
      </button>
    </>
  );
}
